import { useMemo } from "react";
import PropTypes from "prop-types";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import configs from "examples/Charts/PieChart/configs";

ChartJS.register(ArcElement, Tooltip, Legend);

function PieChart({ icon, title, description, height, chart, type }) {
  // Ensure chart is defined and has valid structure
  const safeChart = chart || {};
  const safeLabels = safeChart.labels || [];
  const safeDatasets = safeChart.datasets || {
    label: "",
    backgroundColors: [],
    data: [],
  };

  // Handle case when there is no data
  const isEmptyData = !safeDatasets.data || safeDatasets.data.length === 0;

  // Get chart configuration
  const { data, options } = configs(safeLabels, safeDatasets, type);

  const renderChart = (
    <MDBox py={2} pr={2} pl={icon?.component ? 1 : 2}>
      {title || description ? (
        <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
          {icon?.component && (
            <MDBox
              width="4rem"
              height="4rem"
              bgColor={icon?.color || "dark"}
              variant="gradient"
              coloredShadow={icon?.color || "dark"}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-5}
              mr={2}
            >
              <Icon fontSize="medium">{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox mt={icon?.component ? -2 : 0}>
            {title && <MDTypography variant="h6">{title}</MDTypography>}
            <MDBox mb={2}>
              <MDTypography component="div" variant="button" color="text">
                {description}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
      {useMemo(
        () =>
          isEmptyData ? (
            <MDBox
              height={height}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <MDTypography variant="button" color="text">
                No data available
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox height={height}>
              <Pie data={data} options={options} redraw />
            </MDBox>
          ),
        [chart, height]
      )}
    </MDBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of PieChart
PieChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: "19.125rem",
  chart: {
    labels: [],
    datasets: { label: "", backgroundColors: [], data: [] },
  },
};

// Typechecking props for the PieChart
PieChart.propTypes = {
  icon: PropTypes.shape({
    color: PropTypes.oneOf(["dark"]),
    component: PropTypes.node,
  }),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    datasets: PropTypes.shape({
      label: PropTypes.string,
      backgroundColors: PropTypes.arrayOf(PropTypes.string),
      data: PropTypes.arrayOf(PropTypes.number),
    }),
  }),
};

export default PieChart;
